import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function MagicLink() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const { consumeMagicLink } = useAuth();
    const navigate = useNavigate();
    const [state, setState] = useState({ status: "loading", message: "" });
    const consumedRef = useRef(false);

    useEffect(() => {
        if (!token) {
            setState({ status: "error", message: "Missing sign-in token." });
            return;
        }
        if (consumedRef.current) return;
        consumedRef.current = true;

        (async () => {
            // Optional preview for nicer UX (also catches expired/used early)
            try {
                await api.get(`/auth/magic-link/${token}`);
            } catch (e) {
                setState({
                    status: "error",
                    message: formatApiError(e.response?.data?.detail) || "This sign-in link is no longer valid.",
                });
                return;
            }
            const ok = await consumeMagicLink(token);
            if (ok) {
                setState({ status: "success", message: "Signed in. Taking you to your dashboard…" });
                setTimeout(() => navigate("/", { replace: true }), 600);
            } else {
                setState({ status: "error", message: "We couldn't sign you in with that link. Please request a new one." });
            }
        })();
    }, [token, consumeMagicLink, navigate]);

    return (
        <div className="min-h-screen bg-ink-50 flex items-center justify-center p-6" data-testid="magic-link-page">
            <div className="w-full max-w-md bg-white border border-ink-300 p-10">
                <Logo tone="light" className="h-7 mb-8" showSub />
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Magic link</div>

                {state.status === "loading" && (
                    <>
                        <h2 className="font-editorial text-3xl text-ink-900 mb-2">Signing you in…</h2>
                        <p className="text-sm text-ink-500">Verifying your secure sign-in link.</p>
                        <div className="mt-6 h-0.5 w-full bg-ink-200 overflow-hidden">
                            <div className="h-full w-1/3 bg-haevn-700 animate-pulse" />
                        </div>
                    </>
                )}

                {state.status === "success" && (
                    <>
                        <h2 className="font-editorial text-3xl text-ink-900 mb-2" data-testid="magic-link-success">You&apos;re in.</h2>
                        <p className="text-sm text-ink-500">{state.message}</p>
                    </>
                )}

                {state.status === "error" && (
                    <>
                        <h2 className="font-editorial text-3xl text-ink-900 mb-2" data-testid="magic-link-error">Link unavailable</h2>
                        <p className="text-sm text-ink-500 mb-6">{state.message}</p>
                        <Link
                            to="/login"
                            data-testid="magic-link-back-to-login"
                            className="inline-block bg-haevn-700 text-white px-5 py-2.5 rounded-sm text-sm hover:bg-haevn-900"
                        >
                            Request a new link
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
